// React Imports
import React, { useState , useEffect} from "react";
import { useNavigate } from "react-router-dom";

// MUI Imports
import {
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardHeader,
  MenuItem,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Backdrop,
  Popover,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro , useGridApiRef} from '@mui/x-data-grid-pro';

// ** Icon Imports
import Icon from "../../components/icon";
import AddIcon from "@mui/icons-material/Add";

// ** Custom Component Import
import CustomTextField from "../../components/mui/text-field";

// ** Dialog Import
import BatchSendResultDialog from "./component/BatchSendResult";

// ** Layout Import
import UserLayout from "../../components/layouts/UserLayout";

// ** Logic Imports
import { useIndexLogic } from "./logic/index-logic";
import { useInvoicePermission } from "./permissions/InvoicePermissions";
import ImportExportOptions from "components/ImportExportOptions";

const Invoice = () => {
  const navigate = useNavigate();
  
  const { IndexProps } = useIndexLogic();
  const {
    columns,
    contentHeightFixed,
    handleClearSelection,
    handleGridSelection,
    handleSearch,
    handleClearSearch,
    loading,
    filteredRows,
    setFilteredRows,
    searchText,
    data,
    paginationText,
    paginationModel,
    setPaginationModel,
    selectedRows,
    rowSelectionModel,
    setRowSelectionModel,
    filteredReadyToSendInv,
    selectedButton,
    filterButtons,
    handleFilterChange,
    confirmBatchSend,
    openBatchSendDlg,
    handleCloseBatchSendDlg,
    successfulInvoices,
    failedInvoices
  } = IndexProps;

  const { InvoicePermissionProps } = useInvoicePermission();
  const {
    hasInvoiceSavePermission,
    hasInvoiceGetPermission,
    hasInvoiceDeletePermission,
    hasInvoiceSendEmailPermission
  } = InvoicePermissionProps;

  const apiRef = useGridApiRef();
  useEffect(() => {
      if (!apiRef.current) return;
  
      apiRef.current.autosizeColumns({
        includeHeaders: true,
        includeOutliers: true,
        expand: false,
      });
    }, [filteredRows, columns, apiRef]);

  const isRowSelectable = React.useCallback((params) => {
    return params.row.status !== 'Canceled';
  }, []);

  return (
    <UserLayout contentHeightFixed={contentHeightFixed} activeLink={"Invoices"}>
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: "flex", gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* {filterButtons.map((button, index) => (
                <Button
                  key={index}
                  color={selectedButton === index ? "main" : "secondary"}
                  variant={selectedButton === index ? "contained" : "text"}
                  onClick={() => handleFilterChange(index)}
                >
                  {button.name}
                </Button>
              ))}
              <Divider orientation='vertical' flexItem sx={{mx: 4}} /> */}
              <Box
                sx={{
                  border: "1px solid #D9D9D9",
                  borderRadius: 1,
                  px: 4,
                  py: 2,
                  maxWidth: "max-content",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Typography variant="body2" sx={{ pointerEvents: "none" }}>
                  {filteredRows.length === (data || []).length
                    ? `Displaying ${paginationText} records`
                    : `Displaying ${paginationText} records`}
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <CustomTextField
                value={searchText}
                placeholder="Search"
                disabled={!hasInvoiceGetPermission}
                {...(hasInvoiceGetPermission && {
                  onChange: (e) => handleSearch(e.target.value),
                })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="tabler:search" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchText && (
                        <IconButton
                          edge="end"
                          onClick={handleClearSearch}
                          aria-label="clear"
                          sx={{ mr: 2 }}
                        >
                          <Icon icon="tabler:x" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                color="main"
                endIcon={<AddIcon />}
                disabled={!hasInvoiceSavePermission}
                {...(hasInvoiceSavePermission && {
                  onClick: () => navigate("/invoices/create"),
                })}
              >
                New Invoice
              </Button>
              {/* {hasInvoiceSavePermission && <OptionsButton />} */}
              <ImportExportOptions 
                handleOpenBulkImportDlg={()=>{}}                          
                data={filteredRows}
                module="Invoices"
              />                
            </Box>
          }
          sx={{
            py: 4,
            flexDirection: ["column", "row"],
            "& .MuiCardHeader-action": { m: 0 },
            alignItems: ["flex-start", "center"],
          }}
        />
        {selectedRows.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 4,
              alignItems: "center",
              mx: 6,
              mt: 2,
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #E7E7E7",
                borderRadius: 1,
                pr: 4,
              }}
            >
              <IconButton sx={{ mx: 1 }} onClick={handleClearSelection}>
                <Icon icon="tabler:x" />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  cursor: "default",
                  userSelect: "none",
                }}
              >
                {selectedRows.length}{" "}
                {selectedRows.length === 1 ? "Record" : "Records"} Selected
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="main"
              disabled={!hasInvoiceSendEmailPermission}
              {...hasInvoiceSendEmailPermission && {
                onClick: () => confirmBatchSend()
              }}
            >
              Batch Send
            </Button>
          </Box>
        )}
        <Box sx={{ height: "75vh" }}>
          <DataGridPro
            // pagination
            apiRef={apiRef}
            // rows={selectedButton === 1 ? filteredReadyToSendInv : filteredRows}
            rows={filteredRows}
            rowHeight={62}
            columns={columns}
            // checkboxSelection={selectedButton === 1 ? true : false}
            checkboxSelection
            isRowSelectable={isRowSelectable}
            // pageSizeOptions={[
            //   10,
            //   50,
            //   { value: filteredRows.length, label: "Show All" },
            // ]}
            disableRowSelectionOnClick
            // paginationModel={paginationModel}
            // onPaginationModelChange={setPaginationModel}
            slots={{
              footer: () => null
            }}
            selectionModel={rowSelectionModel}
            onSelectionModelChange={(newSelectionModel) => {
              handleGridSelection(newSelectionModel);
              setRowSelectionModel(newSelectionModel);
            }}
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "none",
              },
            }}
          />
        </Box>
      </Card>

      <BatchSendResultDialog
        open={openBatchSendDlg}
        handleClose={handleCloseBatchSendDlg}
        failedInvoices={failedInvoices}
        successfulInvoices={successfulInvoices}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </UserLayout>
  );
};

export default Invoice;
